import type { CatalogueKey } from '@mahallu/shared';
import type { IconName } from './components/Icon';

export interface NavLeaf {
  key: CatalogueKey;
  path: string;
}
export interface NavGroup {
  key: CatalogueKey;
  icon: IconName;
  path?: string;
  items?: NavLeaf[];
}
export interface NavSection {
  section: CatalogueKey;
  groups: NavGroup[];
}

const soon = (key: CatalogueKey): NavLeaf => ({ key, path: `/soon/${key.replace(/^nav\./, '').replace(/\./g, '-')}` });

/** Side panel: the only sub-navigation (CampusTrack rule). Built screens have real paths. */
export const NAV: NavSection[] = [
  { section: 'nav.overview', groups: [{ key: 'nav.dashboard', icon: 'dash', path: '/' }] },
  {
    section: 'nav.community',
    groups: [
      {
        key: 'nav.register',
        icon: 'users',
        items: [
          { key: 'nav.register.members', path: '/register/members' },
          soon('nav.register.lifeEvents'),
          soon('nav.register.pravasi'),
          { key: 'nav.register.wards', path: '/register/wards' },
          { key: 'nav.register.privacyRequests', path: '/register/privacy-requests' },
          { key: 'nav.register.dataHealth', path: '/register/data-health' },
          soon('nav.register.transfers'),
          soon('nav.register.documents'),
          soon('nav.register.idCards'),
          soon('nav.register.selfRegistration'),
        ],
      },
      {
        key: 'nav.varisankhya',
        icon: 'wallet',
        items: [
          { key: 'nav.varisankhya.dues', path: '/varisankhya/dues' },
          { key: 'nav.varisankhya.collect', path: '/varisankhya/collect' },
          { key: 'nav.varisankhya.receipts', path: '/varisankhya/receipts' },
          { key: 'nav.varisankhya.plans', path: '/varisankhya/plans' },
        ],
      },
      {
        key: 'nav.certificates',
        icon: 'file',
        items: [
          { key: 'nav.certificates.requests', path: '/certificates/requests' },
          { key: 'nav.certificates.issued', path: '/certificates/issued' },
        ],
      },
      { key: 'nav.madrasa', icon: 'book', items: [soon('nav.madrasa.students'), soon('nav.madrasa.attendance')] },
    ],
  },
  {
    section: 'nav.engage',
    groups: [
      {
        key: 'nav.communication',
        icon: 'mega',
        items: [
          { key: 'nav.communication.announcements', path: '/communication/announcements' },
          { key: 'nav.communication.send', path: '/communication/send' },
          { key: 'nav.communication.delivery', path: '/communication/delivery' },
        ],
      },
      { key: 'nav.notifications', icon: 'bell', path: '/notifications' },
      { key: 'nav.approvals', icon: 'check', path: '/approvals' },
    ],
  },
  {
    section: 'nav.administration',
    groups: [
      { key: 'nav.accounts', icon: 'ledger', path: soon('nav.accounts').path },
      { key: 'nav.committee', icon: 'building', path: soon('nav.committee').path },
      {
        key: 'nav.settings',
        icon: 'gear',
        items: [
          { key: 'nav.settings.profile', path: '/settings/profile' },
          { key: 'nav.settings.users', path: '/settings/users' },
          { key: 'nav.settings.sequences', path: '/settings/sequences' },
          { key: 'nav.settings.privacy', path: '/settings/privacy' },
          { key: 'nav.settings.app', path: '/settings/app' },
          { key: 'nav.settings.audit', path: '/settings/audit' },
        ],
      },
    ],
  },
];

/** Finds the nav entry for a path, with its group, for the header breadcrumb. */
export function findNav(pathname: string): { group?: NavGroup; leaf?: NavLeaf } {
  for (const s of NAV) {
    for (const g of s.groups) {
      if (g.path === pathname) return { group: g };
      const leaf = g.items?.find((i) => i.path === pathname);
      if (leaf) return { group: g, leaf };
    }
  }
  return {};
}
